import React, { createContext, useContext, useState, useCallback } from 'react';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  // State for tracking conversation update counts
  const [conversationUpdateCounts, setConversationUpdateCounts] = useState(new Map());
  const [tabUpdateCounts, setTabUpdateCounts] = useState(new Map());
  
  // State for tracking chat notifications (for sidebar indicator — Contact Center)
  const [chatNotificationCount, setChatNotificationCount] = useState(0);
  const [hasChatNotifications, setHasChatNotifications] = useState(false);

  // Internal Chat sidebar (separate from Contact Center)
  const [internalChatNotificationCount, setInternalChatNotificationCount] = useState(0);
  const [hasInternalChatNotifications, setHasInternalChatNotifications] = useState(false);

  const normalizeConversationId = useCallback((conversationId) => {
    const numericId = Number(conversationId);
    if (!Number.isNaN(numericId) && Number.isFinite(numericId)) {
      return numericId;
    }
    return String(conversationId);
  }, []);

  // Increment update count for a specific conversation
  const incrementConversationUpdateCount = useCallback((conversationId) => {
    const key = normalizeConversationId(conversationId);
    setConversationUpdateCounts(prev => {
      const newMap = new Map(prev);
      const currentCount = newMap.get(key) || 0;
      newMap.set(key, currentCount + 1);
      return newMap;
    });
  }, [normalizeConversationId]);

  // Increment update count for a specific tab
  const incrementTabUpdateCount = useCallback((tabName) => {
    setTabUpdateCounts(prev => {
      const newMap = new Map(prev);
      const currentCount = newMap.get(tabName) || 0;
      const newCount = currentCount + 1;
      newMap.set(tabName, newCount);
      return newMap;
    });
  }, []);
  
  // Increment chat notification (for sidebar indicator — Contact Center)
  const incrementChatNotification = useCallback(() => {
    setChatNotificationCount(prev => prev + 1);
    setHasChatNotifications(true);
  }, []);

  const incrementInternalChatNotification = useCallback(() => {
    setInternalChatNotificationCount((prev) => prev + 1);
    setHasInternalChatNotifications(true);
  }, []);

  // Clear conversation update count (when user opens the conversation)
  const clearConversationUpdateCount = useCallback((conversationId) => {
    const key = normalizeConversationId(conversationId);
    setConversationUpdateCounts(prev => {
      const newMap = new Map(prev);
      newMap.delete(key);
      return newMap;
    });
  }, [normalizeConversationId]);

  // Clear tab update count (when user switches to that tab)
  const clearTabUpdateCount = useCallback((tabName) => {
    setTabUpdateCounts(prev => {
      const newMap = new Map(prev);
      newMap.delete(tabName);
      return newMap;
    });
  }, []);
  
  // Clear chat notifications (when user navigates to chat page)
  const clearChatNotifications = useCallback(() => {
    setChatNotificationCount(0);
    setHasChatNotifications(false);
  }, []);

  const clearInternalChatNotifications = useCallback(() => {
    setInternalChatNotificationCount(0);
    setHasInternalChatNotifications(false);
  }, []);

  // Clear all update counts
  const clearAllUpdateCounts = useCallback(() => {
    setConversationUpdateCounts(new Map());
    setTabUpdateCounts(new Map());
    setChatNotificationCount(0);
    setHasChatNotifications(false);
    setInternalChatNotificationCount(0);
    setHasInternalChatNotifications(false);
  }, []);

  // Get conversation update count
  const getConversationUpdateCount = useCallback((conversationId) => {
    const key = normalizeConversationId(conversationId);
    return conversationUpdateCounts.get(key) || 0;
  }, [conversationUpdateCounts, normalizeConversationId]);

  // Get tab update count
  const getTabUpdateCount = useCallback((tabName) => {
    return tabUpdateCounts.get(tabName) || 0;
  }, [tabUpdateCounts]);
  
  // Get chat notification count
  const getChatNotificationCount = useCallback(() => {
    return chatNotificationCount;
  }, [chatNotificationCount]);

  // Check if conversation has updates
  const hasConversationUpdates = useCallback((conversationId) => {
    const key = normalizeConversationId(conversationId);
    return conversationUpdateCounts.has(key) && conversationUpdateCounts.get(key) > 0;
  }, [conversationUpdateCounts, normalizeConversationId]);

  // Check if tab has updates
  const hasTabUpdates = useCallback((tabName) => {
    return tabUpdateCounts.has(tabName) && tabUpdateCounts.get(tabName) > 0;
  }, [tabUpdateCounts]);

  const value = {
    // State
    conversationUpdateCounts,
    tabUpdateCounts,
    chatNotificationCount,
    hasChatNotifications,
    internalChatNotificationCount,
    hasInternalChatNotifications,

    // Actions
    incrementConversationUpdateCount,
    incrementTabUpdateCount,
    incrementChatNotification,
    incrementInternalChatNotification,
    clearConversationUpdateCount,
    clearTabUpdateCount,
    clearChatNotifications,
    clearInternalChatNotifications,
    clearAllUpdateCounts,
    
    // Getters
    getConversationUpdateCount,
    getTabUpdateCount,
    getChatNotificationCount,
    hasConversationUpdates,
    hasTabUpdates
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
